import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  Users, 
  Shield, 
  Calendar,
  Clock,
  CheckCircle,
  CheckCheck,
  ArrowRight,
  Building,
  Briefcase,
  HeartHandshake,
  Brain,
  TrendingUp,
  Zap,
  Mic,
  Send,
  FileText,
  CheckSquare,
  Plus,
  User,
  GripVertical,
  AlertCircle,
  UserCheck,
  Mail,
  Search,
  Bot
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const superAssistantTasks = [
  { icon: Mail, text: "Gérer tes emails" },
  { icon: MessageSquare, text: "Répondre aux messages clients" },
  { icon: Calendar, text: "Organiser les rendez-vous" },
  { icon: Search, text: "Aider à la prospection" },
  { icon: Briefcase, text: "Préparer les dossiers" },
  { icon: Bot, text: "Superviser l'équipe d'Agents IA" },
];

const capabilities = [
  "participer à des réunions",
  "suivre des formations",
  "apprendre les méthodes de l'entreprise",
  "utiliser les outils du client",
];

const orchestratorTasks = [
  "organise les tâches",
  "supervise les actions",
  "vérifie les résultats",
  "coordonne les agents IA",
];

const securityFeatures = [
  "un poste de travail professionnel",
  "une connexion stable",
  "un encadrement",
  "un superviseur",
];

const benefits = [
  { icon: Clock, title: "Gain de temps", description: "Déléguez les tâches chronophages" },
  { icon: Calendar, title: "Meilleure organisation", description: "Structure et processus clairs" },
  { icon: Zap, title: "Plus de réactivité", description: "Réponses rapides à tes clients" },
  { icon: Brain, title: "Moins de charge mentale", description: "Un cerveau externe qui pense pour toi" },
  { icon: TrendingUp, title: "Plus d'opportunités", description: "Du temps pour développer ton activité" },
];

export default function SuperAssistantPage() {
  return (
    <div data-testid="super-assistant-page" className="min-h-screen bg-white">
      <Header />
      
      {/* Hero - Style Fonctionnalités en bleu */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-ba-primary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <UserCheck className="w-4 h-4" />
              Super Assistant
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Ton Super Assistant{' '}
              <span className="text-ba-primary">a mi-temps ou plein temps</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-4 leading-relaxed">
              Chaque entrepreneur beneficie d'un assistant a <strong className="text-slate-800">mi-temps ou plein temps</strong> selon ses besoins.
              Il devient progressivement un veritable <strong className="text-slate-800">bras droit</strong> dans ton organisation.
            </p>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-10">
              Contrairement a de nombreux services externalises, cet assistant travaille <strong className="text-slate-800">specifiquement pour ton entreprise</strong>.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-primary hover:bg-ba-primary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Decouvrir le Super Assistant
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Ce que ton Super Assistant peut faire */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Ce que ton Super Assistant peut faire pour toi
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un assistant polyvalent qui s'adapte à tes besoins
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {superAssistantTasks.map((task, index) => {
              const Icon = task.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-6 bg-slate-50 rounded-2xl"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-ba-primary" />
                  </div>
                  <span className="text-slate-700 font-medium">{task.text}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 2 - Membre de l'équipe */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-rm-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
                Un membre à part entière de ton équipe
              </h2>
              <p className="text-lg text-rm-text-secondary mb-6">
                Le Super Assistant fonctionne comme un <strong className="text-rm-text">collaborateur interne</strong>. 
                Avec le temps, il devient un membre intégré à l'organisation.
              </p>
              <p className="text-rm-text-secondary mb-4">Il peut :</p>
              <div className="space-y-3">
                {capabilities.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-rm-text-secondary">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-rm-accent/20 to-rm-primary/10 rounded-3xl p-8 flex items-center justify-center"
            >
              <div className="text-center">
                <HeartHandshake className="w-24 h-24 text-rm-primary mx-auto mb-4" />
                <p className="text-xl font-semibold text-rm-text">Un vrai collaborateur</p>
                <p className="text-rm-text-secondary">Pas un simple prestataire</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3 - Messagerie BatiAssist */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              {/* Maquette de la messagerie BatiAssist */}
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                {/* Header de la messagerie */}
                <div className="bg-white border-b border-slate-100 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-rm-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-rm-text">Mon Super Assistant</p>
                    <p className="text-xs text-green-500">En ligne</p>
                  </div>
                </div>
                
                {/* Zone de messages */}
                <div className="bg-slate-50 p-4 space-y-4 h-[320px] overflow-y-auto">
                  {/* Date separator */}
                  <div className="text-center">
                    <span className="text-xs bg-white px-3 py-1 rounded-full text-slate-500 shadow-sm">
                      Aujourd'hui
                    </span>
                  </div>
                  
                  {/* Message reçu */}
                  <div className="flex justify-start">
                    <div className="max-w-[75%]">
                      <p className="text-xs text-slate-500 mb-1 ml-3">Sarah - Super Assistant</p>
                      <div className="bg-white rounded-2xl rounded-bl-md shadow-sm px-4 py-2">
                        <p className="text-sm text-rm-text">Bonjour ! J'ai bien reçu ta demande de devis pour M. Dupont. Je m'en occupe tout de suite. 👍</p>
                        <div className="flex justify-end mt-1">
                          <span className="text-xs text-slate-400">09:32</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Message envoyé */}
                  <div className="flex justify-end">
                    <div className="max-w-[75%]">
                      <div className="bg-rm-primary text-white rounded-2xl rounded-br-md px-4 py-2">
                        <p className="text-sm">Parfait merci ! Peux-tu aussi vérifier mes emails et me faire un résumé ?</p>
                        <div className="flex justify-end items-center gap-1 mt-1">
                          <span className="text-xs text-white/70">09:35</span>
                          <CheckCheck className="w-3 h-3 text-white/70" />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Message reçu avec fichier */}
                  <div className="flex justify-start">
                    <div className="max-w-[75%]">
                      <p className="text-xs text-slate-500 mb-1 ml-3">Sarah - Super Assistant</p>
                      <div className="bg-white rounded-2xl rounded-bl-md shadow-sm px-4 py-2">
                        <p className="text-sm text-rm-text mb-2">C'est fait ! Voici le résumé de tes 5 emails importants :</p>
                        <div className="bg-slate-100 rounded-lg p-2 flex items-center gap-2">
                          <div className="w-8 h-8 bg-rm-primary/10 rounded flex items-center justify-center">
                            <FileText className="w-4 h-4 text-rm-primary" />
                          </div>
                          <span className="text-xs text-slate-600 truncate">Resume_emails_11mars.pdf</span>
                        </div>
                        <div className="flex justify-end mt-1">
                          <span className="text-xs text-slate-400">09:42</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Barre d'envoi */}
                <div className="bg-white border-t border-slate-100 p-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200">
                    <FileText className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200">
                    <Mic className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 bg-slate-50 rounded-full px-4 py-2 text-sm text-slate-400">
                    Écris ton message...
                  </div>
                  <div className="w-8 h-8 rounded-full bg-rm-primary flex items-center justify-center">
                    <Send className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <MessageSquare className="w-8 h-8 text-rm-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
                Messagerie instantanée intégrée
              </h2>
              <p className="text-lg text-rm-text-secondary mb-6">
                Communiquez directement avec ton assistant via la <strong className="text-rm-text">messagerie BatiAssist</strong> intégrée à ton espace client :
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                  <MessageSquare className="w-6 h-6 text-rm-primary" />
                  <span className="text-rm-text">Messages texte en temps réel</span>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                  <Mic className="w-6 h-6 text-rm-primary" />
                  <span className="text-rm-text">Notes vocales transcrites</span>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                  <FileText className="w-6 h-6 text-rm-primary" />
                  <span className="text-rm-text">Partage de fichiers et documents</span>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                  <Zap className="w-6 h-6 text-rm-primary" />
                  <span className="text-rm-text">Notifications instantanées</span>
                </div>
              </div>
              <p className="text-rm-text-secondary mt-6">
                Tout est centralisé dans <strong className="text-rm-text">ton espace client BatiAssist</strong>. Pas besoin d'applications tierces.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3bis - Espace Tâches */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-rm-secondary/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <CheckSquare className="w-8 h-8 text-rm-secondary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
              Déléguez tes tâches en un clic
            </h2>
            <p className="text-lg text-rm-text-secondary max-w-3xl mx-auto">
              Crée des tâches pour ton assistant et suivez leur avancement en temps réel grâce au <strong className="text-rm-text">tableau Kanban intégré</strong>.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Kanban Board Mockup */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-lg font-semibold text-rm-text mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-rm-secondary rounded-full"></span>
                Tableau Kanban - Suivi des tâches
              </h3>
              <div className="bg-slate-50 rounded-2xl p-4 shadow-lg border border-slate-200">
                <div className="grid grid-cols-3 gap-3">
                  {/* Colonne À faire */}
                  <div className="bg-slate-100 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-200 text-slate-600">À faire</span>
                      <span className="text-xs text-slate-400">3</span>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-white rounded-lg p-2 shadow-sm">
                        <div className="flex items-start gap-1">
                          <GripVertical className="w-3 h-3 text-slate-300 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-rm-text">Relancer devis Dupont</p>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-400"></span>
                              <span className="text-[10px] text-slate-400">Haute</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm">
                        <div className="flex items-start gap-1">
                          <GripVertical className="w-3 h-3 text-slate-300 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-rm-text">Commander matériaux</p>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                              <span className="text-[10px] text-slate-400">Moyenne</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm opacity-60">
                        <div className="flex items-start gap-1">
                          <GripVertical className="w-3 h-3 text-slate-300 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-rm-text">Appeler fournisseur</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Colonne En cours */}
                  <div className="bg-blue-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-600">En cours</span>
                      <span className="text-xs text-slate-400">2</span>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-white rounded-lg p-2 shadow-sm border-l-2 border-blue-400">
                        <div className="flex items-start gap-1">
                          <GripVertical className="w-3 h-3 text-slate-300 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-rm-text">Préparer planning semaine</p>
                            <div className="flex items-center gap-2 mt-1">
                              <User className="w-3 h-3 text-slate-400" />
                              <span className="text-[10px] text-slate-500">Sarah</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm border-l-2 border-blue-400">
                        <div className="flex items-start gap-1">
                          <GripVertical className="w-3 h-3 text-slate-300 mt-0.5" />
                          <div>
                            <p className="text-xs font-medium text-rm-text">Répondre emails</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              <span className="text-[10px] text-slate-500">Aujourd'hui</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Colonne Terminée */}
                  <div className="bg-green-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-600">Terminée</span>
                      <span className="text-xs text-slate-400">4</span>
                    </div>
                    <div className="space-y-2">
                      <div className="bg-white rounded-lg p-2 shadow-sm opacity-75">
                        <div className="flex items-start gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5" />
                          <p className="text-xs text-slate-500 line-through">Envoyer facture Martin</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm opacity-75">
                        <div className="flex items-start gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5" />
                          <p className="text-xs text-slate-500 line-through">Confirmer RDV chantier</p>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg p-2 shadow-sm opacity-60">
                        <div className="flex items-start gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500 mt-0.5" />
                          <p className="text-xs text-slate-500 line-through">Mise à jour CRM</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Task Creation Form Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-lg font-semibold text-rm-text mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-rm-primary rounded-full"></span>
                Créer une tâche en quelques secondes
              </h3>
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-lg font-bold text-rm-text">Nouvelle tâche</h4>
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Plus className="w-4 h-4 text-slate-400" />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Titre *</label>
                    <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-rm-text">
                      Relancer le client Dupont pour le devis piscine
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
                    <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-500 h-16">
                      Appeler M. Dupont pour savoir s'il a des questions sur le devis envoyé la semaine dernière...
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Assigner à</label>
                      <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm flex items-center gap-2">
                        <div className="w-5 h-5 bg-rm-primary/10 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-rm-primary" />
                        </div>
                        <span className="text-rm-text">Sarah - Assistant</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1">Priorité</label>
                      <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-orange-500" />
                        <span className="text-orange-500">Haute</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Date d'échéance</label>
                    <div className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="text-rm-text">15 mars 2026</span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">
                      Annuler
                    </button>
                    <button className="px-4 py-2 text-sm bg-rm-primary text-white rounded-lg font-medium">
                      Créer la tâche
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Features list */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 grid md:grid-cols-4 gap-4"
          >
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-rm-text">Drag & drop intuitif</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-rm-text">Priorités visuelles</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-rm-text">Assignation à l'assistant</span>
            </div>
            <div className="flex items-center gap-3 bg-slate-50 rounded-xl p-4">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-rm-text">Rappels automatiques</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 4 - Chef d'orchestre */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <Brain className="w-8 h-8 text-rm-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
              Le chef d'orchestre des agents IA
            </h2>
            <p className="text-lg text-rm-text-secondary max-w-3xl mx-auto">
              Le Super Assistant pilote l'équipe d'assistants digitaux pour créer une organisation <strong className="text-rm-text">humain + intelligence artificielle</strong>.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {orchestratorTasks.map((task, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-rm-primary/5 to-rm-accent/10 rounded-2xl p-6 text-center border border-rm-primary/10"
              >
                <div className="w-12 h-12 bg-rm-primary text-white rounded-xl flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                  {index + 1}
                </div>
                <p className="text-rm-text font-medium capitalize">{task}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 - Sécurité */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-rm-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
                Environnement de travail sécurisé
              </h2>
              <p className="text-lg text-rm-text-secondary mb-6">
                Les assistants travaillent depuis des <strong className="text-rm-text">locaux sécurisés à Madagascar</strong>.
              </p>
              <p className="text-rm-text-secondary mb-4">Ils disposent :</p>
              <div className="space-y-3">
                {securityFeatures.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-rm-text-secondary">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-8 shadow-lg border border-slate-100"
            >
              <Building className="w-16 h-16 text-rm-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-rm-text text-center mb-4">Locaux professionnels</h3>
              <p className="text-rm-text-secondary text-center">
                Un environnement de travail structuré pour garantir qualité et fiabilité.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section - Nos bureaux à Madagascar */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <Users className="w-8 h-8 text-rm-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-4">
              Nos bureaux à Madagascar
            </h2>
            <p className="text-lg text-rm-text-secondary max-w-3xl mx-auto">
              Découvre l'équipe qui travaille chaque jour pour accompagner ton entreprise.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/m24dtqj7_WhatsApp%20Image%202025-11-24%20at%2010.30.24.jpeg"
                  alt="Équipe BatiAssist - Espace de travail collaboratif"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 bg-white">
                <p className="text-sm text-rm-text-secondary text-center">Espace de travail collaboratif</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/k44k3hj7_WhatsApp%20Image%202025-11-24%20at%2010.28.16.jpeg"
                  alt="Équipe BatiAssist - Notre équipe dédiée"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 bg-white">
                <p className="text-sm text-rm-text-secondary text-center">Notre équipe dédiée</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_organize-smart/artifacts/l03gar95_WhatsApp%20Image%202025-11-24%20at%2010.29.32.jpeg"
                  alt="Équipe BatiAssist - Environnement professionnel"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 bg-white">
                <p className="text-sm text-rm-text-secondary text-center">Environnement professionnel</p>
              </div>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center text-rm-text-secondary mt-8 max-w-2xl mx-auto"
          >
            Nos assistants travaillent dans un environnement moderne et professionnel, 
            équipés des meilleurs outils pour toi accompagner efficacement.
          </motion.p>
        </div>
      </section>

      {/* Section 6 - Suivi quotidien */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-rm-primary/5 to-rm-accent/10 rounded-3xl p-8 md:p-12 border border-rm-primary/10"
          >
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Calendar className="w-8 h-8 text-rm-primary" />
              </div>
              <div>
                <h2 className="text-2xl md:text-4xl font-bold text-rm-text mb-6">
                  Suivi quotidien
                </h2>
                <p className="text-lg text-rm-text-secondary mb-4">Chaque jour :</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-rm-text-secondary">les tâches sont organisées</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-rm-text-secondary">les actions sont suivies</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-rm-text-secondary">un compte rendu peut être envoyé</span>
                  </div>
                </div>
                <p className="text-rm-text font-medium">
                  Cela permet à l'entrepreneur de garder une <strong>vision claire</strong> de ce qui est fait.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 7 - Disponibilité */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 bg-rm-primary/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <Clock className="w-8 h-8 text-rm-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-6">
              Disponibilité et organisation
            </h2>
            <p className="text-lg text-rm-text-secondary mb-4">
              Le Super Assistant travaille environ <strong className="text-rm-text">40 heures par semaine</strong>.
            </p>
            <p className="text-lg text-rm-text-secondary">
              Lorsqu'il ne travaille pas (soirées ou week-ends), un <strong className="text-rm-text">assistant digital peut rester disponible</strong> via la messagerie BatiAssist pour préparer certaines demandes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 8 - Comparaison */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-4">
              Comparaison avec un recrutement classique
            </h2>
            <p className="text-lg text-rm-text-secondary">
              Pourquoi choisir BatiAssist plutôt qu'un recrutement traditionnel ?
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Recrutement France */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-100 rounded-3xl p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Briefcase className="w-8 h-8 text-slate-500" />
                <h3 className="text-xl font-bold text-rm-text">Recruter en France</h3>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-slate-400">•</span>
                  <span className="text-rm-text-secondary">Salaire</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-400">•</span>
                  <span className="text-rm-text-secondary">Charges sociales</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-400">•</span>
                  <span className="text-rm-text-secondary">Bureau</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-400">•</span>
                  <span className="text-rm-text-secondary">Matériel</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-slate-400">•</span>
                  <span className="text-rm-text-secondary">Gestion administrative</span>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4">
                <p className="text-sm text-rm-text-secondary mb-1">Coût total estimé</p>
                <p className="text-2xl font-bold text-slate-600">3 500 - 4 000 € <span className="text-sm font-normal">/mois</span></p>
              </div>
            </motion.div>

            {/* BatiAssist */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-rm-primary to-rm-primary/80 rounded-3xl p-8 text-white"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold">R</span>
                </div>
                <h3 className="text-xl font-bold">Avec BatiAssist</h3>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300" />
                  <span>Super Assistant mi-temps / plein temps</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300" />
                  <span>Environ 40 heures par semaine</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300" />
                  <span>Équipe digitale incluse</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300" />
                  <span>Automatisations</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-300" />
                  <span>Aucune gestion RH</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-sm text-white/80 mb-1">À partir de</p>
                <p className="text-2xl font-bold">949 € <span className="text-sm font-normal">/mois</span></p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 9 - Bénéfices */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-rm-text mb-4">
              Bénéfices pour l'entrepreneur
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-rm-accent/20 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-rm-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-rm-text mb-2">{benefit.title}</h3>
                  <p className="text-rm-text-secondary text-sm">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 10 - CTA Final */}
      <section className="py-20 bg-rm-primary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Découvre comment ton Super Assistant peut transformer ton organisation
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Réserve un entretien gratuit avec Romain pour discuter de ton projet.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-white hover:bg-white/90 text-ba-primary font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105 group"
              >
                Réserver un entretien avec Romain
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
